import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, File as FileIcon } from 'lucide-react';
import { getPublicFile, publicFileContentURL, type PubFileRef } from '@/api/publicDrive';

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Public single-file viewer (131.34). Unauthenticated; download by token. */
export function PublicDriveFilePage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [file, setFile] = useState<PubFileRef | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPublicFile(token)
      .then((f) => {
        if (!cancelled) setFile(f);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-lop-bg-deep)] p-4 text-[var(--color-lop-text-primary)]">
      <div className="w-full max-w-md rounded-lg border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] p-8">
        {loading && <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('publicDrive.loading')}</p>}
        {!loading && error && <p className="text-sm text-[var(--color-lop-danger)]">{t('publicDrive.invalid')}</p>}
        {!loading && !error && file && (
          <div className="flex flex-col items-center gap-4 text-center">
            <FileIcon className="h-12 w-12 text-[var(--color-lop-teal)]" />
            <div>
              <p className="text-lg font-semibold">{file.name}</p>
              {file.sizeBytes != null && (
                <p className="text-xs text-[var(--color-lop-text-muted)]">{fmtSize(file.sizeBytes)}</p>
              )}
            </div>
            <a
              href={publicFileContentURL(token)}
              className="inline-flex items-center gap-2 rounded-[6px] bg-[var(--color-lop-teal)] px-4 py-2 text-sm font-medium text-[var(--color-lop-bg-deep)] hover:opacity-90"
              download
            >
              <Download className="h-4 w-4" />
              {t('publicDrive.download')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
